<template>
  <div class="pb-10">
    <!-- Filtres -->
    <BaseCard class="mb-5">
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Recherche -->
        <div class="flex-1 min-w-[250px]">
          <BaseInput
            v-model="search"
            type="search"
            placeholder="Rechercher un produit..."
            clearable
            @input="onSearchInput">
            <template #icon-left>
              <i class="fa-solid fa-search"></i>
            </template>
          </BaseInput>
        </div>

        <!-- Filtre catégorie -->
        <div class="relative md:w-52" v-click-outside="closeCategoryDropdown">
          <div class="relative">
            <i class="fas fa-tag absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
            <input
              v-model="categorySearchTerm"
              type="text"
              placeholder="Catégorie..."
              class="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-gray-50 transition-colors"
              @focus="showCategoryDropdown = true"
              @input="filterCategories" />
            <button v-if="selectedCategory"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              @click="clearCategoryFilter">
              <i class="fas fa-times text-[10px]"></i>
            </button>
            <button v-else
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              @click="showCategoryDropdown = !showCategoryDropdown">
              <i class="fas fa-chevron-down text-[10px]"></i>
            </button>
          </div>

          <!-- Dropdown catégories -->
          <div v-if="showCategoryDropdown"
            class="absolute top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto z-50">
            <div v-if="filteredCategories.length === 0" class="px-4 py-3 text-xs text-gray-400">
              Aucune catégorie trouvée
            </div>
            <div
              @click="selectCategory('')"
              class="px-4 py-2.5 text-sm cursor-pointer border-b border-gray-50 transition-colors"
              :class="selectedCategory === '' ? 'bg-primary/5 text-primary font-medium' : 'hover:bg-gray-50/60'">
              Toutes les catégories
            </div>
            <div
              v-for="category in filteredCategories"
              :key="category.id"
              @click="selectCategory(category.id)"
              class="px-4 py-2.5 text-sm cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
              :class="selectedCategory === category.id ? 'bg-primary/5 text-primary font-medium' : 'hover:bg-gray-50/60'">
              {{ category.name }}
            </div>
          </div>
        </div>

        <!-- Bouton Ajouter -->
        <button v-permission="'products.create'"
          class="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          @click="onAddProduct">
          <i class="fas fa-plus text-xs"></i>
          Ajouter un produit
        </button>
      </div>
    </BaseCard>

    <!-- Alerte stock faible -->
    <div v-if="lowStockProducts && lowStockProducts.length > 0"
      class="flex items-center justify-between gap-3 px-4 py-3 mb-5 bg-amber-50 border border-amber-100 rounded-xl">
      <div class="flex items-center gap-2.5">
        <span class="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-triangle-exclamation text-amber-500 text-[10px]"></i>
        </span>
        <span class="text-sm font-medium text-amber-800">
          {{ lowStockProducts.length }} produit{{ lowStockProducts.length > 1 ? 's en' : ' en' }} stock faible
        </span>
      </div>
      <button
        class="text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors whitespace-nowrap"
        @click="toggleLowStockFilter">
        {{ showLowStock ? 'Voir tous' : 'Filtrer' }}
      </button>
    </div>

    <!-- Filtres actifs -->
    <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mb-5">
      <span class="text-xs text-gray-400">Filtres:</span>
      <span v-if="search"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
        "{{ search }}"
        <button @click="clearSearch" class="hover:text-blue-900 transition-colors">
          <i class="fas fa-times text-[9px]"></i>
        </button>
      </span>
      <span v-if="selectedCategoryName"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-medium">
        {{ selectedCategoryName }}
        <button @click="clearCategoryFilter" class="hover:text-violet-900 transition-colors">
          <i class="fas fa-times text-[9px]"></i>
        </button>
      </span>
      <span v-if="showLowStock"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
        Stock faible
        <button @click="toggleLowStockFilter" class="hover:text-amber-900 transition-colors">
          <i class="fas fa-times text-[9px]"></i>
        </button>
      </span>
      <button class="text-xs text-gray-400 hover:text-gray-600 ml-auto transition-colors" @click="clearAllFilters">
        Tout effacer
      </button>
    </div>

    <!-- Chargement -->
    <BaseCard v-if="loading" padding="none">
      <div class="flex flex-col items-center justify-center py-16">
        <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
        <p class="text-sm text-gray-400">Chargement des produits...</p>
      </div>
    </BaseCard>

    <!-- État vide -->
    <BaseCard v-else-if="displayedProducts.length === 0" padding="none">
      <div class="flex flex-col items-center justify-center py-16 text-center px-6">
        <div class="text-gray-300 mb-4">
          <i class="fas fa-box-open text-5xl"></i>
        </div>
        <h3 class="text-base font-semibold text-gray-700 mb-1">Aucun produit trouvé</h3>
        <p class="text-sm text-gray-400 max-w-sm mb-6">
          <span v-if="hasActiveFilters">Aucun produit ne correspond à vos critères.</span>
          <span v-else-if="showLowStock">Tous vos produits ont un stock suffisant.</span>
          <span v-else>Utilisez le bouton "Ajouter un produit" pour commencer.</span>
        </p>
        <button v-if="hasActiveFilters"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          @click="clearAllFilters">
          Réinitialiser les filtres
        </button>
      </div>
    </BaseCard>

    <!-- Tableau des produits -->
    <BaseCard v-else padding="none">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50/80 border-b border-gray-200">
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Produit
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Référence
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Stock
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Catégorie
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Prix TTC
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Prix HT
              </th>
              <th class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="product in displayedProducts"
              :key="product.id"
              class="hover:bg-gray-50/80 transition-colors"
              :class="product.stock_quantity != null && product.stock_quantity <= 5 ? 'bg-amber-50/30' : ''">
              <!-- Image -->
              <td class="px-5 py-3.5">
                <div class="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <img
                    v-if="product.image_url"
                    :src="product.image_url"
                    :alt="product.name"
                    class="w-full h-full object-cover" />
                  <i v-else class="fas fa-image text-gray-300 text-sm"></i>
                </div>
              </td>

              <!-- Nom -->
              <td class="px-5 py-3.5">
                <span
                  class="text-sm font-semibold text-gray-900 hover:text-primary cursor-pointer transition-colors"
                  @click="goToProductDetails(product)">
                  {{ product.name }}
                </span>
              </td>

              <!-- Référence -->
              <td class="px-5 py-3.5">
                <span v-if="product.reference" class="text-sm text-gray-500 font-mono">{{ product.reference }}</span>
                <span v-else class="text-xs text-gray-300 italic">-</span>
              </td>

              <!-- Stock -->
              <td class="px-5 py-3.5">
                <span :class="[
                  'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                  product.stock_quantity == null || product.stock_quantity === 0
                    ? 'bg-red-50 text-red-600'
                    : product.stock_quantity <= 5
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-green-50 text-green-700'
                ]">
                  {{ product.stock_quantity ?? 0 }}
                </span>
              </td>

              <!-- Catégorie -->
              <td class="px-5 py-3.5">
                <span v-if="getCategoryName(product.category_id)"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                  {{ getCategoryName(product.category_id) }}
                </span>
                <span v-else class="text-xs text-gray-300">-</span>
              </td>

              <!-- Prix TTC -->
              <td class="px-5 py-3.5">
                <span class="text-sm font-bold text-gray-900">{{ formatPrice(product.price_incl_tax) }}</span>
              </td>

              <!-- Prix HT -->
              <td class="px-5 py-3.5 text-sm text-gray-500">
                {{ formatPrice(product.price_excl_tax) }}
              </td>

              <!-- Actions -->
              <td class="px-5 py-3.5">
                <div class="flex items-center justify-end gap-1">
                  <button
                    class="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Ajuster le stock"
                    @click.stop="openStockModal(product)">
                    <i class="fas fa-boxes-stacked text-sm"></i>
                  </button>
                  <button
                    class="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                    title="Voir les détails"
                    @click.stop="goToProductDetails(product)">
                    <i class="fas fa-eye text-sm"></i>
                  </button>
                  <button
                    class="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                    title="Modifier"
                    @click.stop="onEditProduct(product)">
                    <i class="fas fa-pen text-sm"></i>
                  </button>
                  <button
                    class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                    @click.stop="onDeleteProduct(product)">
                    <i class="fas fa-trash text-sm"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="displayedProducts.length > 0" class="border-t border-gray-100 px-5 py-4">
        <PaginationComponent
          :current-page="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="pagination.total"
          :to="pagination.to"
          :from="pagination.from"
          @page-change="handlePageChange"
        />
      </div>
    </BaseCard>

    <!-- Stock Adjustment Modal -->
    <StockAdjustmentModal
      :show="showStockModal"
      :product="selectedProductForStock"
      @close="closeStockModal"
      @updated="onStockUpdated"
    />
  </div>
</template>

<script>
import BaseCard from '@/components/base/BaseCard.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import StockAdjustmentModal from '@/components/products/StockAdjustmentModal.vue'

export default {
  name: 'ProductsList',
  components: {
    BaseCard,
    BaseInput,
    PaginationComponent,
    StockAdjustmentModal
  },
  props: {
    products: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        perPage: 10,
        total: 0,
        lastPage: 1,
        from: null,
        to: null
      })
    },
    loading: {
      type: Boolean,
      default: false
    },
    categories: {
      type: Array,
      default: () => []
    },
    lowStockProducts: {
      type: Array,
      default: () => []
    }
  },
  emits: ['page-change', 'search-change', 'category-change', 'low-stock-toggle', 'add-product', 'edit-product', 'delete-product', 'stock-updated'],
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.body.addEventListener('click', el.clickOutsideEvent)
      },
      unmounted(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  },
  data() {
    return {
      filteredCategories: [],
      search: '',
      selectedCategory: '',
      categorySearchTerm: '',
      showCategoryDropdown: false,
      showLowStock: false,
      searchTimeout: null,
      showStockModal: false,
      selectedProductForStock: null
    }
  },
  computed: {
    displayedProducts() {
      if (!Array.isArray(this.products)) return []
      return this.products
    },
    hasActiveFilters() {
      return this.search || this.selectedCategory || this.showLowStock
    },
    selectedCategoryName() {
      if (!this.selectedCategory) return ''
      const category = this.categories.find(c => c.id === this.selectedCategory)
      return category ? category.name : ''
    }
  },
  watch: {
    categories: {
      immediate: true,
      handler(newCategories) {
        this.filteredCategories = Array.isArray(newCategories) ? [...newCategories] : []
      }
    }
  },
  methods: {
    onSearchInput() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.$emit('search-change', this.search)
      }, 300)
    },
    filterCategories() {
      const search = this.categorySearchTerm.toLowerCase()
      this.filteredCategories = this.categories.filter(cat =>
        cat.name.toLowerCase().includes(search)
      )
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId
      this.showCategoryDropdown = false
      this.categorySearchTerm = categoryId ? this.getCategoryName(categoryId) : ''
      this.$emit('category-change', categoryId)
    },
    clearCategoryFilter() {
      this.selectedCategory = ''
      this.categorySearchTerm = ''
      this.filteredCategories = this.categories
      this.$emit('category-change', '')
    },
    closeCategoryDropdown() {
      this.showCategoryDropdown = false
    },
    toggleLowStockFilter() {
      this.showLowStock = !this.showLowStock
      this.$emit('low-stock-toggle', this.showLowStock)
    },
    clearSearch() {
      this.search = ''
      this.$emit('search-change', '')
    },
    clearAllFilters() {
      this.search = ''
      this.selectedCategory = ''
      this.categorySearchTerm = ''
      this.showLowStock = false
      this.$emit('search-change', '')
      this.$emit('category-change', '')
      this.$emit('low-stock-toggle', false)
    },
    getCategoryName(categoryId) {
      const category = this.categories.find(c => c.id === categoryId)
      return category ? category.name : ''
    },
    formatPrice(price) {
      if (!price) return '0 FCFA'
      return `${Number(price).toLocaleString('fr-FR')} FCFA`
    },
    goToProductDetails(product) {
      this.$router.push(`/products/${product.id}`)
    },
    onAddProduct() {
      this.$emit('add-product')
    },
    onEditProduct(product) {
      this.$emit('edit-product', product)
    },
    onDeleteProduct(product) {
      this.$emit('delete-product', product)
    },
    handlePageChange(page) {
      this.$emit('page-change', page)
    },
    openStockModal(product) {
      this.selectedProductForStock = product
      this.showStockModal = true
    },
    closeStockModal() {
      this.showStockModal = false
      this.selectedProductForStock = null
    },
    onStockUpdated(updatedProduct) {
      this.$emit('stock-updated', updatedProduct)
    }
  }
}
</script>
